import React, { useState, useMemo } from 'react';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
  Label,
} from 'recharts';
import { AlertTriangle, X, ChevronRight, Target } from 'lucide-react';

// Types
export type RiskSeverity = 'critical' | 'high' | 'medium' | 'low';

export interface RiskFinding {
  id: string;
  description: string;
  severity: RiskSeverity;
  impact: number; // 1-10 scale
  likelihood: number; // 1-10 scale
  contractId?: string;
  articleId?: string;
  articleNumber?: string;
  articleTitle?: string;
  cfrCitation?: string;
  clauseReference?: string;
  recommendation?: string;
}

export interface RiskPriorityMatrixProps {
  risks: RiskFinding[];
  onRiskClick?: (risk: RiskFinding) => void;
  onViewInContract?: (risk: RiskFinding) => void;
  testId?: string;
}

// Color configuration based on severity - using design system colors
const SEVERITY_COLORS: Record<RiskSeverity, { fill: string; stroke: string; label: string; bgClass: string }> = {
  critical: { fill: '#dc2626', stroke: '#b91c1c', label: 'Critical', bgClass: 'bg-danger-500' },
  high: { fill: '#d97706', stroke: '#b45309', label: 'High', bgClass: 'bg-warning-600' },
  medium: { fill: '#f59e0b', stroke: '#d97706', label: 'Medium', bgClass: 'bg-warning-500' },
  low: { fill: '#22c55e', stroke: '#16a34a', label: 'Low', bgClass: 'bg-success-500' },
};

// Quadrant configuration - used for documentation/reference
// Critical: high impact + high likelihood (top-right)
// High: high impact + low likelihood (top-left)
// Medium: low impact + high likelihood (bottom-right)
// Low: low impact + low likelihood (bottom-left)

// Custom tooltip component
interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    payload: RiskFinding;
  }>;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload }) => {
  if (!active || !payload || !payload.length) {
    return null;
  }

  const risk = payload[0].payload;
  const severityConfig = SEVERITY_COLORS[risk.severity];

  return (
    <div
      className="chart-tooltip"
      data-testid="risk-matrix-tooltip"
    >
      <div className="flex items-center gap-2 mb-2">
        <span
          className="chart-legend-dot"
          style={{ backgroundColor: severityConfig.fill }}
        />
        <span className="chart-tooltip-header !mb-0">
          {severityConfig.label} Risk
        </span>
      </div>
      <p className="text-sm text-neutral-700 mb-3 line-clamp-3">{risk.description}</p>
      <div className="chart-tooltip-divider" />
      <div className="flex gap-4 text-xs text-neutral-500">
        <div>
          <span className="font-medium">Impact:</span> {risk.impact}/10
        </div>
        <div>
          <span className="font-medium">Likelihood:</span> {risk.likelihood}/10
        </div>
      </div>
      {risk.articleNumber && (
        <div className="mt-2 text-xs text-neutral-500">
          Article {risk.articleNumber}: {risk.articleTitle}
        </div>
      )}
      <div className="chart-tooltip-footer">Click to view details</div>
    </div>
  );
};

// Risk detail modal component
interface RiskDetailModalProps {
  risk: RiskFinding;
  onClose: () => void;
  onViewInContext?: () => void;
}

const RiskDetailModal: React.FC<RiskDetailModalProps> = ({ risk, onClose, onViewInContext }) => {
  const severityConfig = SEVERITY_COLORS[risk.severity];

  return (
    <div
      className="chart-modal-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="risk-detail-modal"
    >
      <div className="chart-modal max-w-lg">
        {/* Header */}
        <div className="chart-modal-header flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ backgroundColor: `${severityConfig.fill}20` }}
            >
              <AlertTriangle
                className="h-5 w-5"
                style={{ color: severityConfig.fill }}
              />
            </div>
            <div>
              <h3 className="font-semibold text-neutral-900">Risk Details</h3>
              <span
                className="text-xs font-medium px-2 py-0.5 rounded-full"
                style={{
                  backgroundColor: `${severityConfig.fill}20`,
                  color: severityConfig.stroke,
                }}
              >
                {severityConfig.label} Severity
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="btn-ghost p-2 rounded-full"
            data-testid="modal-close-button"
          >
            <X className="h-5 w-5 text-neutral-400" />
          </button>
        </div>

        {/* Content */}
        <div className="chart-modal-content space-y-4">
          {/* Description */}
          <div>
            <h4 className="text-sm font-medium text-neutral-500 mb-1">Description</h4>
            <p className="text-neutral-900">{risk.description}</p>
          </div>

          {/* Impact and Likelihood */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-neutral-50 rounded-lg p-3">
              <div className="text-sm font-medium text-neutral-500 mb-1">Impact Score</div>
              <div className="flex items-center gap-2">
                <div className="text-2xl font-bold text-neutral-900">{risk.impact}</div>
                <div className="text-sm text-neutral-500">/10</div>
              </div>
              <div className="score-progress mt-2">
                <div
                  className="score-progress-fill"
                  style={{
                    width: `${risk.impact * 10}%`,
                    backgroundColor: severityConfig.fill,
                  }}
                />
              </div>
            </div>
            <div className="bg-neutral-50 rounded-lg p-3">
              <div className="text-sm font-medium text-neutral-500 mb-1">Likelihood Score</div>
              <div className="flex items-center gap-2">
                <div className="text-2xl font-bold text-neutral-900">{risk.likelihood}</div>
                <div className="text-sm text-neutral-500">/10</div>
              </div>
              <div className="score-progress mt-2">
                <div
                  className="score-progress-fill"
                  style={{
                    width: `${risk.likelihood * 10}%`,
                    backgroundColor: severityConfig.fill,
                  }}
                />
              </div>
            </div>
          </div>

          {/* Location */}
          {(risk.articleNumber || risk.clauseReference) && (
            <div>
              <h4 className="text-sm font-medium text-neutral-500 mb-1">Location</h4>
              <div className="bg-neutral-50 rounded-lg p-3 space-y-1">
                {risk.articleNumber && (
                  <p className="text-neutral-900">
                    Article {risk.articleNumber}: {risk.articleTitle}
                  </p>
                )}
                {risk.clauseReference && (
                  <p className="text-sm text-neutral-600">{risk.clauseReference}</p>
                )}
              </div>
            </div>
          )}

          {/* CFR Citation */}
          {risk.cfrCitation && (
            <div>
              <h4 className="text-sm font-medium text-neutral-500 mb-1">Regulatory Reference</h4>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary-50 text-primary-700 rounded-lg text-sm">
                <Target className="h-4 w-4" />
                {risk.cfrCitation}
              </div>
            </div>
          )}

          {/* Recommendation */}
          {risk.recommendation && (
            <div>
              <h4 className="text-sm font-medium text-neutral-500 mb-1">Recommendation</h4>
              <div className="bg-warning-50 border border-warning-200 rounded-lg p-3">
                <p className="text-warning-900 text-sm">{risk.recommendation}</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="chart-modal-footer flex justify-end gap-3">
          <button
            onClick={onClose}
            className="btn-secondary"
          >
            Close
          </button>
          {onViewInContext && (
            <button
              onClick={onViewInContext}
              className="btn-primary"
              data-testid="view-in-context-button"
            >
              View in Contract
              <ChevronRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Legend component
const Legend: React.FC = () => {
  return (
    <div className="chart-legend" data-testid="risk-matrix-legend">
      <span className="chart-legend-title">Risk Severity:</span>
      {Object.entries(SEVERITY_COLORS).map(([severity, config]) => (
        <div key={severity} className="chart-legend-item" data-testid={`legend-${severity}`}>
          <div
            className="chart-legend-dot border-2"
            style={{ backgroundColor: config.fill, borderColor: config.stroke }}
          />
          <span className="chart-legend-label">{config.label}</span>
        </div>
      ))}
    </div>
  );
};

// Quadrant labels component
const QuadrantLabels: React.FC = () => {
  return (
    <>
      <div
        className="absolute text-xs font-semibold text-danger-600 opacity-70 pointer-events-none"
        style={{ top: '15%', right: '15%' }}
        data-testid="quadrant-critical"
      >
        CRITICAL
      </div>
      <div
        className="absolute text-xs font-semibold text-warning-600 opacity-70 pointer-events-none"
        style={{ top: '15%', left: '15%' }}
        data-testid="quadrant-high"
      >
        HIGH
      </div>
      <div
        className="absolute text-xs font-semibold text-warning-500 opacity-70 pointer-events-none"
        style={{ bottom: '20%', right: '15%' }}
        data-testid="quadrant-medium"
      >
        MEDIUM
      </div>
      <div
        className="absolute text-xs font-semibold text-success-600 opacity-70 pointer-events-none"
        style={{ bottom: '20%', left: '15%' }}
        data-testid="quadrant-low"
      >
        LOW
      </div>
    </>
  );
};

// Main RiskPriorityMatrix component
export const RiskPriorityMatrix: React.FC<RiskPriorityMatrixProps> = ({
  risks,
  onRiskClick,
  onViewInContract,
  testId = 'risk-priority-matrix',
}) => {
  const [selectedRisk, setSelectedRisk] = useState<RiskFinding | null>(null);

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    const stats = {
      total: risks.length,
      critical: 0,
      high: 0,
      medium: 0,
      low: 0,
      avgImpact: 0,
      avgLikelihood: 0,
    };

    if (risks.length === 0) return stats;

    risks.forEach((risk) => {
      stats[risk.severity]++;
      stats.avgImpact += risk.impact;
      stats.avgLikelihood += risk.likelihood;
    });

    stats.avgImpact = Math.round((stats.avgImpact / risks.length) * 10) / 10;
    stats.avgLikelihood = Math.round((stats.avgLikelihood / risks.length) * 10) / 10;

    return stats;
  }, [risks]);

  // Calculate critical quadrant risks (high impact + high likelihood)
  const criticalQuadrantRisks = useMemo(() => {
    return risks.filter((risk) => risk.impact >= 5 && risk.likelihood >= 5);
  }, [risks]);

  const handleRiskClick = (data: any) => {
    const risk = data as RiskFinding;
    setSelectedRisk(risk);
    onRiskClick?.(risk);
  };

  const handleCloseModal = () => {
    setSelectedRisk(null);
  };

  const handleViewInContext = () => {
    if (selectedRisk) {
      onViewInContract?.(selectedRisk);
      setSelectedRisk(null);
    }
  };

  // Custom scatter point shape
  const renderPoint = (props: any) => {
    const { cx, cy, payload } = props;
    const config = SEVERITY_COLORS[payload.severity as RiskSeverity];

    return (
      <circle
        cx={cx}
        cy={cy}
        r={10}
        fill={config.fill}
        stroke={config.stroke}
        strokeWidth={2}
        className="cursor-pointer hover:opacity-80 transition-opacity"
        data-testid={`risk-point-${payload.id}`}
      />
    );
  };

  if (risks.length === 0) {
    return (
      <div
        className="chart-container chart-empty"
        data-testid={`${testId}-empty`}
      >
        <div className="chart-empty-icon">
          <Target className="h-8 w-8 text-neutral-400" />
        </div>
        <h3 className="chart-empty-title">No Risk Data Available</h3>
        <p className="chart-empty-description">
          Run a risk analysis on your contracts to view the priority matrix.
        </p>
      </div>
    );
  }

  return (
    <div className="chart-container" data-testid={testId}>
      {/* Header */}
      <div className="chart-header">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h3 className="chart-title">
              <Target className="h-5 w-5 text-primary-600" />
              Risk Priority Matrix
            </h3>
            <p className="chart-subtitle">
              Impact vs. Likelihood analysis - click on a risk to view details
            </p>
          </div>

          {/* Summary stats */}
          <div className="chart-summary" data-testid="matrix-summary">
            <span className="chart-summary-stat">
              <span className="chart-summary-value">{summaryStats.total}</span> Risks
            </span>
            <div className="flex items-center gap-2">
              {summaryStats.critical > 0 && (
                <span
                  className="chart-badge-critical"
                  data-testid="summary-critical"
                >
                  {summaryStats.critical} Critical
                </span>
              )}
              {summaryStats.high > 0 && (
                <span
                  className="chart-badge-high"
                  data-testid="summary-high"
                >
                  {summaryStats.high} High
                </span>
              )}
            </div>
            {criticalQuadrantRisks.length > 0 && (
              <div className="flex items-center gap-1 text-danger-600" data-testid="critical-quadrant-warning">
                <AlertTriangle className="h-4 w-4" />
                <span className="text-xs font-medium">
                  {criticalQuadrantRisks.length} in critical quadrant
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="chart-content relative">
        <QuadrantLabels />

        <ResponsiveContainer width="100%" height={400}>
          <ScatterChart
            margin={{ top: 20, right: 40, bottom: 40, left: 40 }}
            data-testid="scatter-chart"
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />

            {/* Quadrant background areas - using reference areas would be ideal but we use reference lines for simplicity */}
            <ReferenceLine x={5} stroke="#d4d4d4" strokeWidth={2} strokeDasharray="5 5" />
            <ReferenceLine y={5} stroke="#d4d4d4" strokeWidth={2} strokeDasharray="5 5" />

            <XAxis
              type="number"
              dataKey="likelihood"
              name="Likelihood"
              domain={[0, 10]}
              ticks={[0, 2, 4, 5, 6, 8, 10]}
              tickLine={false}
              axisLine={{ stroke: '#a3a3a3' }}
              tick={{ fill: '#525252', fontSize: 12 }}
            >
              <Label
                value="Likelihood"
                offset={-20}
                position="insideBottom"
                style={{ fill: '#404040', fontWeight: 600, fontSize: 13 }}
              />
            </XAxis>

            <YAxis
              type="number"
              dataKey="impact"
              name="Impact"
              domain={[0, 10]}
              ticks={[0, 2, 4, 5, 6, 8, 10]}
              tickLine={false}
              axisLine={{ stroke: '#a3a3a3' }}
              tick={{ fill: '#525252', fontSize: 12 }}
            >
              <Label
                value="Impact"
                angle={-90}
                offset={15}
                position="insideLeft"
                style={{ fill: '#404040', fontWeight: 600, fontSize: 13, textAnchor: 'middle' }}
              />
            </YAxis>

            <Tooltip
              content={<CustomTooltip />}
              cursor={{ strokeDasharray: '3 3' }}
            />

            <Scatter
              name="Risks"
              data={risks}
              shape={renderPoint}
              onClick={handleRiskClick}
            >
              {risks.map((risk, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={SEVERITY_COLORS[risk.severity].fill}
                  stroke={SEVERITY_COLORS[risk.severity].stroke}
                />
              ))}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>

        {/* Quadrant explanation */}
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="quadrant-explanation">
          <div className="chart-quadrant-card-critical">
            <div className="chart-quadrant-title">
              <div className="chart-quadrant-dot bg-danger-500" />
              <span className="chart-quadrant-label text-danger-700">Critical Priority</span>
            </div>
            <p className="chart-quadrant-description text-danger-600">High Impact + High Likelihood</p>
          </div>
          <div className="chart-quadrant-card-high">
            <div className="chart-quadrant-title">
              <div className="chart-quadrant-dot bg-warning-600" />
              <span className="chart-quadrant-label text-warning-700">High Priority</span>
            </div>
            <p className="chart-quadrant-description text-warning-600">High Impact + Low Likelihood</p>
          </div>
          <div className="chart-quadrant-card-medium">
            <div className="chart-quadrant-title">
              <div className="chart-quadrant-dot bg-warning-500" />
              <span className="chart-quadrant-label text-warning-600">Medium Priority</span>
            </div>
            <p className="chart-quadrant-description text-warning-500">Low Impact + High Likelihood</p>
          </div>
          <div className="chart-quadrant-card-low">
            <div className="chart-quadrant-title">
              <div className="chart-quadrant-dot bg-success-500" />
              <span className="chart-quadrant-label text-success-700">Low Priority</span>
            </div>
            <p className="chart-quadrant-description text-success-600">Low Impact + Low Likelihood</p>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="chart-footer">
        <Legend />
      </div>

      {/* Risk detail modal */}
      {selectedRisk && (
        <RiskDetailModal
          risk={selectedRisk}
          onClose={handleCloseModal}
          onViewInContext={handleViewInContext}
        />
      )}
    </div>
  );
};

export default RiskPriorityMatrix;
